import { IAmbienceMenuState } from "source/app/presentation/Header/Tools/Sound/Ambience/IAmbienceMenuState";
import { HowlerSoundFile } from "source/app/services/Sound/HowlerSoundFile";
import Data from "../../Data";
import { NonRepeatingShuffledArray } from "../../NonRepeatingShuffledArray";
import StaticSingleton from "../../Shared/StaticSingleton";
import Background from "../../UI/Background/Background";
import { ESharedComponent } from "../../UI/State/ESharedComponent";
import { State } from "../../UI/State/State";
import { ISoundFile } from "../ISoundFile";
import { B2FileInfo } from "../Music/BackBlazeFilePath";

class Ambience extends StaticSingleton<Ambience> {
    StartNextAmbience = async () => {
        const nextAmbienceFileInfo = await this.GetNextAmbienceFileInfo();

        if (!nextAmbienceFileInfo) {
            return null;
        }

        this.GetAmbienceMenu().setState({
            currentFileInfo: nextAmbienceFileInfo,
        });
    };

    GetAmbienceSoundFile(b2FileInfo: B2FileInfo): ISoundFile {
        const { filePath } = b2FileInfo;
        const ambienceSoundFile = new HowlerSoundFile(filePath);
        return ambienceSoundFile;
    }

    IsWoodlandAmbiencePlaying() {
        const { currentAmbienceSoundFile, currentFileInfo } =
            this.GetAmbienceMenu().state;

        return (
            currentFileInfo?.fileNameWithoutExtension === "Woodlands" &&
            currentAmbienceSoundFile?.IsPlaying() === true
        );
    }

    StartWoodlandAmbience = async () => {
        const ambienceMenu = this.GetAmbienceMenu();
        ambienceMenu.setState({ selectedAmbience: "Woodlands" }, () => {
            this.Stop();
            void this.StartNextAmbience();
        });
    };

    ToggleWoodlandAmbience = () => {
        const { currentAmbienceSoundFile, currentFileInfo } =
            this.GetAmbienceMenu().state;
        const isWoodlandAmbience =
            currentFileInfo?.fileNameWithoutExtension === "Woodlands";

        if (isWoodlandAmbience && currentAmbienceSoundFile) {
            if (currentAmbienceSoundFile.IsPlaying()) {
                currentAmbienceSoundFile.Pause();
            } else {
                currentAmbienceSoundFile.Resume();
            }
            return;
        }

        void this.StartWoodlandAmbience();
    };

    Stop = () => {
        const { currentAmbienceSoundFile } = this.GetAmbienceMenu().state;

        if (!currentAmbienceSoundFile) {
            return;
        }

        Background.ChangeImage();
        currentAmbienceSoundFile.Stop();

        this.GetAmbienceMenu().setState({
            currentFileInfo: undefined,
            loadingProgressPercent: undefined,
        });
    };

    Duck() {
        const { currentAmbienceSoundFile } = this.GetAmbienceMenu().state;

        if (currentAmbienceSoundFile) {
            console.log(`[Ambience] Ducking`);
            currentAmbienceSoundFile.FadeOut(0.3, 400);
        }
    }

    UnDuck() {
        const { currentAmbienceSoundFile } = this.GetAmbienceMenu().state;

        if (currentAmbienceSoundFile) {
            console.log(`[Ambience] Unducking`);
            currentAmbienceSoundFile.FadeIn(0.3, 1, 600);
        }
    }

    FadeOut(callback?: () => void) {
        const { currentAmbienceSoundFile } = this.GetAmbienceMenu().state;

        if (currentAmbienceSoundFile) {
            currentAmbienceSoundFile.FadeOut(0, 6000, callback);
        }
    }

    FadeIn(callback?: () => void) {
        const { currentAmbienceSoundFile } = this.GetAmbienceMenu().state;

        if (currentAmbienceSoundFile) {
            currentAmbienceSoundFile.FadeIn(0, 0.5, 5000, callback);
        }
    }

    private async GetNextAmbienceFileInfo(): Promise<B2FileInfo | undefined> {
        let cachedB2FileInfos = this.GetAmbienceMenu().state.cachedB2FileInfos;

        if (!cachedB2FileInfos) {
            cachedB2FileInfos = await this.GetReloadedCachedFileInfos();
        }

        const { selectedAmbience } = this.GetAmbienceMenu().state;

        if (!selectedAmbience) {
            const nextSongFileInfo = cachedB2FileInfos!.TakeOne();
            return nextSongFileInfo;
        }

        let nextAmbienceFileInfo;
        let isMatchingSelectedAmbience = false;

        while (!isMatchingSelectedAmbience) {
            nextAmbienceFileInfo = cachedB2FileInfos!.TakeOne();

            isMatchingSelectedAmbience =
                nextAmbienceFileInfo.fileNameWithoutExtension ===
                selectedAmbience;
        }

        return nextAmbienceFileInfo;
    }

    private async GetReloadedCachedFileInfos() {
        const fileUrls: string[] = await Data.LoadFromServer(`files/ambience`);

        let b2fileInfos = fileUrls.map((fileUrl) => new B2FileInfo(fileUrl));

        const cachedB2FileInfos = new NonRepeatingShuffledArray(b2fileInfos);

        this.GetAmbienceMenu().setState({
            allFileInfos: b2fileInfos,
            cachedB2FileInfos,
        });

        return cachedB2FileInfos;
    }

    private GetAmbienceMenu = () =>
        State.ReadClass<IAmbienceMenuState>(ESharedComponent.AmbienceMenu);
}

export default new Ambience();
